/**
 * The item marketplace's percentage fee — decided now, charged later.
 *
 * No listing exists yet and none is created here. What is fixed here is the
 * thing a listing cannot be built without: WHO may sell, and WHAT the sale
 * costs them. Two systems are involved and they must never share a rule:
 *
 * | | Direct trade | Item marketplace |
 * |---|---|---|
 * | VIP | required, both sides | **not** required |
 * | Fee | fixed 200 000 points | percentage of the sale |
 * | Percentage | **never** | 16% VIP, 23% otherwise |
 * | Currency | items, Gold, Cash | items only, priced in Gold or Cash |
 *
 * The direct trade is the RMT tool: both participants pay for VIP, one side may
 * be empty, and the currency offered arrives whole. The marketplace is the open
 * shop: anybody may use it, and the seller pays for the convenience out of what
 * they receive. Conflating them is how "VIP only" leaks onto a marketplace that
 * is meant to be for everybody.
 */

/** The seller's cut of the fee, as whole percentage points. */
export const MARKET_FEE_VIP_PERCENT = 16;
export const MARKET_FEE_NON_VIP_PERCENT = 23;

/** Which schedule a sale is charged under. Resolved from VIP at SETTLEMENT. */
export const MARKET_FEE_TIERS = ['vip', 'standard'] as const;
export type MarketFeeTier = (typeof MARKET_FEE_TIERS)[number];

/**
 * The fee as an exact rational, so a settlement never touches a float.
 *
 * 16% is `4/25` and 23% is `23/100`, each in LOWEST TERMS. Stated the same way
 * the VIP multipliers are, for the same reason: the fee is applied to `bigint`
 * prices, and a percentage that arrives as `0.16` has already lost precision
 * before anybody multiplies by it.
 *
 * Reducing matters for the carry, not for the arithmetic: the remainder is
 * bounded by the DENOMINATOR, so `4/25` carries at most 24 rather than 99 and
 * settles a run of small sales in a quarter of the steps. The fee for any given
 * price is identical either way.
 */
export interface MarketFeeRate {
  readonly tier: MarketFeeTier;
  readonly percent: number;
  readonly numerator: bigint;
  readonly denominator: bigint;
}

export const MARKET_FEE_RATES: Readonly<Record<MarketFeeTier, MarketFeeRate>> = Object.freeze({
  vip: Object.freeze({
    tier: 'vip',
    percent: MARKET_FEE_VIP_PERCENT,
    // 16/100 reduced.
    numerator: 4n,
    denominator: 25n,
  }),
  standard: Object.freeze({
    tier: 'standard',
    percent: MARKET_FEE_NON_VIP_PERCENT,
    // 23/100 is already in lowest terms.
    numerator: 23n,
    denominator: 100n,
  }),
});

/** The schedule a seller is charged under, from their VIP state alone. */
export function marketFeeRateFor(vipActive: boolean): MarketFeeRate {
  return vipActive ? MARKET_FEE_RATES.vip : MARKET_FEE_RATES.standard;
}

/** What a sale is split into. The buyer always pays exactly `gross`. */
export interface MarketSaleSplit {
  /** What the buyer pays. Never reduced, never increased by the fee. */
  readonly gross: bigint;
  /** What the platform destroys or keeps. Floored; see `carry`. */
  readonly fee: bigint;
  /** What the seller receives. `gross - fee`, always. */
  readonly net: bigint;
  /**
   * The fractional remainder of the fee, to be carried forward.
   *
   * A 10 Gold sale at 16% owes 1.6 Gold of fee. Flooring the fee every time
   * would quietly hand the seller the difference on every small sale, and over
   * a thousand of them that is a real, growing leak. The remainder is returned
   * here so a future settlement can accumulate it exactly the way the VIP bonus
   * accumulates its own — see the carry contract below.
   */
  readonly carry: bigint;
}

/**
 * Splits a sale, exactly, with the remainder handed back rather than dropped.
 *
 * The buyer pays `gross` whatever the seller's tier is: the fee comes out of
 * what the SELLER receives and never out of the buyer's pocket, so the same
 * item costs the same to buy from a VIP and a non-VIP.
 *
 * `carry` must be fed back in on the seller's next sale of the same currency
 * and the same tier. That triple is the key, and it has to be all three:
 *
 * - **seller**, because one player's rounding must never fund another's;
 * - **currency**, because a Gold remainder is not a Cash remainder;
 * - **fee tier**, because VIP and standard owe different fractions, and mixing
 *   the two would let a player bank a remainder at 16% and spend it at 23%.
 *
 * A future `market_fee_carry` table keyed on exactly that triple is what makes
 * "16%" mean 16% across a thousand small sales rather than approximately 16%.
 */
export function splitMarketSale(gross: bigint, rate: MarketFeeRate, carry = 0n): MarketSaleSplit {
  if (gross < 0n) {
    throw new RangeError(`A sale price cannot be negative, received ${gross}`);
  }
  if (carry < 0n || carry >= rate.denominator) {
    throw new RangeError(
      `A market fee carry must be in [0, ${rate.denominator}), received ${carry}`,
    );
  }
  const scaled = gross * rate.numerator + carry;
  const fee = scaled / rate.denominator;
  return { gross, fee, net: gross - fee, carry: scaled % rate.denominator };
}
